import { BackendType, HttpResponse, commonDaoFactory, coreDaoFactory } from '../common'
import { CompletedWorkoutFilter, WorkoutFilter } from '../service/core-service'
import { errorUtil } from '../util/error-util'
import Joi from 'joi'
import { validationUtil } from 'src/util/validation-util'

const dao = coreDaoFactory({
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})

/**
 * Unauthenticated sibling of `dao`, for the one public Core route — `GET /workouts/featured`.
 *
 * It deliberately omits `authorizationHeaderInterceptor` and `refreshTokenInterceptor`, which
 * `coreDaoFactory` adds. The featured shelf renders on the login page, which sits outside
 * `SessionContext` and has no session at all: sending a bearer there is pointless, and a
 * signed-out visitor triggering the 401-refresh flow on a public route is a bug.
 *
 * Built through `commonDaoFactory` rather than a bare `axios.create` so the base-URL
 * resolution (`VITE_API_BASE_URL` with a `window.location.origin` fallback, plus the
 * `/api/core/v2/` prefix) stays in one place.
 */
const publicDao = commonDaoFactory({
  backendType: BackendType.CORE,
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})
export interface WorkoutsMeta {
  page?: number
  pageSize?: number
  totalPages?: number
  totalSize?: number
}
export const coreDao = {
  ...dao,
  getWorkouts: async (filter?: WorkoutFilter): Promise<Workout[]> => {
    const url = '/workouts'
    const result = await dao._http.get<HttpResponse<Workout>>(url, { params: filter })
    return result.data.data
  },
  // Same `/workouts` endpoint as getWorkouts, but surfaces the pagination `meta`
  // (page/pageSize/totalSize/totalPages) the Results view needs for the count header + pagination.
  getWorkoutsWithMeta: async (filter?: WorkoutFilter): Promise<{ data: Workout[]; meta?: WorkoutsMeta }> => {
    const url = '/workouts'
    const result = await dao._http.get<HttpResponse<Workout>>(url, { params: filter })
    return result.data
  },
  getSuggestedWorkouts: async (): Promise<Workout[]> => {
    const url = '/workouts/suggested'
    const result = await dao._http.get<HttpResponse<Workout>>(url)
    return result.data.data
  },
  /**
   * The permanently-free set. Public — goes through `publicDao`, never `dao`.
   * Already ordered shortest-to-longest by the server; do not re-sort.
   */
  getFeaturedWorkouts: async (): Promise<FeaturedWorkout[]> => {
    const url = '/workouts/featured'
    const result = await publicDao._http.get<HttpResponse<FeaturedWorkout>>(url)
    return result.data.data
  },
  getForDayWorkouts: async (filter?: WorkoutFilter): Promise<Workout[]> => {
    const url = '/workouts/for-day'
    const result = await dao._http.get<HttpResponse<Workout>>(url, { params: filter })
    return result.data.data
  },
  getForWeekWorkouts: async (filter?: WorkoutFilter): Promise<WorkoutSummary[]> => {
    const url = '/workouts/for-week'
    const result = await dao._http.get<HttpResponse<WorkoutSummary>>(url, { params: filter })
    return result.data.data
  },
  getWorkoutById: async (workoutId: string): Promise<Workout> => {
    const url = `/workouts/${workoutId}`
    const result = await dao._http.get<HttpResponse<Workout>>(url)
    return result.data.data[0]
  },
  getWorkoutTypes: async (): Promise<WorkoutType[]> => {
    const url = '/workout-types'
    const result = await dao._http.get<HttpResponse<WorkoutType>>(url)
    return result.data.data
  },
  listWorkoutStyles: async (): Promise<WorkoutStyle[]> => {
    const url = '/workout-styles'
    const result = await dao._http.get<HttpResponse<WorkoutStyle>>(url)
    return result.data.data
  },
  getBodyParts: async (): Promise<BodyPart[]> => {
    const url = '/body-parts'
    const result = await dao._http.get<HttpResponse<WorkoutType>>(url)
    return result.data.data
  },
  completeWorkout: async (workoutId: string): Promise<CompletedWorkout> => {
    const url = `/completed-workouts`
    const result = await dao._http.post<HttpResponse<CompletedWorkout>>(url, { workoutId })
    return result.data.data[0]
  },
  getHustlerMe: async (): Promise<Hustler> => {
    const url = `/hustlers/me`
    const result = await dao._http.get<HttpResponse<Hustler>>(url)
    return result.data.data[0]
  },
  getConfig: async (): Promise<HustlerConfig> => {
    const url = `/hustlers/me/config`
    const result = await dao._http.get<HttpResponse<HustlerConfig>>(url)
    return result.data.data[0]
  },
  patchHustlerMe: async (params: { profile: HustlerProfile }): Promise<Hustler> => {
    const url = `/hustlers/me`
    const result = await dao._http.patch<HttpResponse<Hustler>>(url, params)
    return result.data.data[0]
  },
  validatePromoCode: async (code: string): Promise<PromoCodeValidation> => {
    const url = `/payments/promo-codes/validate`
    const result = await dao._http.get<HttpResponse<PromoCodeValidation>>(url, {
      params: { code },
    })
    return result.data.data[0]
  },
  createCheckoutSession: async (
    priceId: string,
    promotionCode?: string,
    options?: { skipErrorInterceptor?: boolean },
  ): Promise<string> => {
    const url = `/payments/checkout-session`
    const result = await dao._http.post<HttpResponse<string>>(
      url,
      { priceId, ...(promotionCode ? { promotionCode } : {}) },
      options?.skipErrorInterceptor ? ({ skipErrorInterceptor: true } as any) : undefined,
    )
    return result.data.data[0]
  },
  createPortalSession: async (): Promise<string> => {
    const url = `/payments/portal-session`
    const result = await dao._http.post<HttpResponse<string>>(url)
    return result.data.data[0]
  },
  getPrices: async (): Promise<Price[]> => {
    const url = `/payments/prices`
    const result = await dao._http.get<HttpResponse<Price>>(url)
    return result.data.data
  },
  getMySubscriptions: async (): Promise<StoreSubscription | undefined> => {
    const url = `/payments/me/subscriptions`
    const result = await dao._http.get<HttpResponse<StoreSubscription | undefined>>(url)
    return result.data.data[0]
  },
  getDetailedSubscriptions: async (): Promise<Subscription[]> => {
    const url = `/payments/me/subscriptions/detailed`
    const result = await dao._http.get<HttpResponse<Subscription>>(url)
    return result.data.data
  },
  getTrainers: async (): Promise<Trainer[]> => {
    const url = `/trainers`
    const result = await dao._http.get<HttpResponse<Trainer>>(url)
    return result.data.data
  },
  getTrainer: async (params: { trainerId: string }): Promise<Trainer> => {
    const { trainerId } = params
    const url = `/trainers/${trainerId}`
    const result = await dao._http.get<HttpResponse<Trainer>>(url)
    return result.data.data[0]
  },
  getCompletedWorkouts: async (params?: { filter?: CompletedWorkoutFilter }): Promise<CompletedWorkout[]> => {
    const { filter } = params ?? {}
    const url = '/completed-workouts?view=web'
    const result = await dao._http.get<HttpResponse<CompletedWorkout>>(url, { params: filter })
    return result.data.data
  },
  getWorkoutCompletedByHustlers: async (params: {
    workoutId: string
    page?: number
    pageSize?: number
    include?: string[]
  }): Promise<CompletedByHustlersResponse> => {
    const { workoutId, page = 1, pageSize = 20, include = [] } = params
    const url = `/workouts/${workoutId}/completed-by`
    const result = await dao._http.get<HttpResponse<HustlerCompletedWorkout>>(url, {
      params: {
        page,
        pageSize,
        include: include.join(','),
      },
    })
    const meta = result.data.meta as any
    return {
      data: result.data.data,
      page: meta.page,
      pageSize: meta.pageSize,
      totalSize: meta.totalSize,
      totalPages: meta.totalPages,
    }
  },
  getLegalTermsAndConditions: async (): Promise<string> => {
    const url = '/legal/terms-and-conditions'
    const result = await dao._http.get<HttpResponse<string>>(url)
    return result.data.data[0]
  },
  getLegalPrivacyPolicy: async (): Promise<string> => {
    const url = '/legal/privacy-policy'
    const result = await dao._http.get<HttpResponse<string>>(url)
    return result.data.data[0]
  },
  getWeekPromoVideo: async (): Promise<WeekPromoVideo> => {
    const url = '/week-promo-video/current'
    const result = await dao._http.get<HttpResponse<WeekPromoVideo>>(url)
    return result.data.data[0]
  },
  getWeeklyImage: async (params: { dayInWeek: number }): Promise<WeeklyImage> => {
    const { dayInWeek } = params
    const url = `/images/weekly/${dayInWeek}`
    const result = await dao._http.get<HttpResponse<WeeklyImage>>(url)
    return result.data.data[0]
  },
  getProgrammes: async (): Promise<Programme[]> => {
    const url = `/programmes`
    const result = await dao._http.get<HttpResponse<Programme>>(url)
    return result.data.data
  },
  getIntensity: async (params: { id: string }): Promise<Intensity> => {
    const { id } = params
    const url = `/intensities/${id}`
    const result = await dao._http.get<HttpResponse<Intensity>>(url)
    return result.data.data[0]
  },
  getProgrammeDuration: async (params: { programmeId: string }): Promise<ProgrammeDuration> => {
    const { programmeId } = params
    const url = `/programmes/${programmeId}/duration`
    const result = await dao._http.get<HttpResponse<ProgrammeDuration>>(url)
    return result.data.data[0]
  },
  listProgrammeIntensities: async (): Promise<Intensity[]> => {
    const url = `/programme-intensities`
    const result = await dao._http.get<HttpResponse<Intensity>>(url)
    return result.data.data
  },
  listWorkoutIntensities: async (): Promise<Intensity[]> => {
    const url = `/workout-intensities`
    const result = await dao._http.get<HttpResponse<Intensity>>(url)
    return result.data.data
  },
  getMyStartedProgrammes: async (): Promise<HustlerProgramme[]> => {
    const result = await dao._http.get<HttpResponse<HustlerProgramme>>('/hustlers/me/programmes')
    return result.data.data.map((hp) => {
      return validationUtil.validate(hp, hustlerProgrammeSchema)
    })
  },
  getMyStartedProgrammesFromLibrary: async (params: {
    page: number
    pageSize: number
  }): Promise<StartedProgrammesFromLibrary> => {
    const { page, pageSize } = params
    const result = await dao._http.get<HttpResponse<HustlerProgramme>>('/hustlers/me/programmes', {
      params: {
        enrollmentSource: 'library',
        page,
        pageSize,
      },
    })
    const meta = result.data.meta as {
      page: number
      pageSize: number
      totalSize: number
      totalPages: number
    }
    return {
      data: result.data.data.map((hp) => validationUtil.validate(hp, hustlerProgrammeSchema)),
      page: meta.page,
      pageSize: meta.pageSize,
      totalSize: meta.totalSize,
      totalPages: meta.totalPages,
    }
  },
  getMyCompletedProgrammes: async (): Promise<HustlerProgramme[]> => {
    const result = await dao._http.get<HttpResponse<HustlerProgramme>>('/hustlers/me/completed-programmes')
    return result.data.data.map((hp) => {
      return validationUtil.validate(hp, hustlerProgrammeSchema)
    })
  },
  startProgramme: async ({ programmeId, enrollmentSource }: StartProgrammeParams): Promise<HustlerProgramme> => {
    const result = await dao._http.post('/hustlers/me/programmes', {
      programmeId,
      ...(enrollmentSource !== undefined ? { enrollmentSource } : {}),
    })
    const data = result.data.data[0]
    if (!data) throw new Error('Unable to start programme')
    return validationUtil.validate(data, hustlerProgrammeSchema)
  },
  getSuggestedSeries: async (): Promise<SuggestedSeries | undefined> => {
    const result = await dao._http.get<HttpResponse<SuggestedSeries>>('/hustlers/me/programmes/suggested')
    return result.data.data[0]
  },
  dismissSuggestedSeries: async (programmeId: string): Promise<void> => {
    const url = `/hustlers/me/programmes/suggested/${programmeId}/dismiss`
    await dao._http.post<HttpResponse<unknown>>(url)
  },
  cancelMyProgramme: async (programmeId: string): Promise<void> => {
    const url = `/hustlers/me/programmes/${programmeId}/cancel`
    await dao._http.post<HttpResponse<unknown>>(url)
  },
  resetSuggestedSeriesFlow: async (): Promise<void> => {
    await dao._http.post<HttpResponse<unknown>>('/hustlers/me/programmes/suggested/reset')
  },
  completeTour: async (): Promise<void> => {
    await dao._http.post<HttpResponse<unknown>>('/hustlers/me/app-tour/complete')
  },
  getProgrammeById: async (programmeId: string): Promise<Programme> => {
    const url = `/programmes/${programmeId}`
    const result = await dao._http.get<HttpResponse<Programme>>(url)
    const data = result.data.data[0]
    if (!data) throw new Error('Programme not found')
    return data
  },
  getProgrammeWorkouts: async (programmeId: string): Promise<ProgrammeWorkout[]> => {
    const url = `/programmes/${programmeId}/programme-workouts`
    const result = await dao._http.get<HttpResponse<ProgrammeWorkout>>(url)
    return result.data.data
  },
  getProgrammeWorkoutsAvailability: async (params: { programmeId: string }): Promise<ProgrammeWorkoutsAvailability[]> => {
    const { programmeId } = params
    const url = `/programmes/${programmeId}/programme-workouts-availability`
    const result = await dao._http.get<HttpResponse<ProgrammeWorkoutsAvailability>>(url)
    return result.data.data
  },
  completeProgrammeWorkout: async (params: {
    programmeId: string
    programmeWorkoutId: string
    maxHeartRate: number
    calories: number
    averageHeartRate: number
    hustleScore: number
  }): Promise<CompletedProgrammeWorkout> => {
    const { programmeId, programmeWorkoutId, maxHeartRate, calories, averageHeartRate, hustleScore } = params
    const url = `/hustlers/me/programmes/${programmeId}/programme-workouts/${programmeWorkoutId}/complete`
    const result = await dao._http.post<HttpResponse<CompletedProgrammeWorkout>>(url, {
      maxHeartRate,
      calories,
      averageHeartRate,
      hustleScore,
    })
    const data = result.data.data[0]
    if (!data) throw new Error('Unable to complete programme workout')
    return data
  },
  getMyCompletedProgrammeWorkoutsFromProgramme: async (programmeId: string): Promise<CompletedProgrammeWorkout[]> => {
    const url = `/hustlers/me/programmes/${programmeId}/completed-programme-workouts`
    const result = await dao._http.get<HttpResponse<CompletedProgrammeWorkout>>(url)
    return result.data.data
  },
  getAllAvailableProgrammeAwards: async (params: { programmeId: string }): Promise<Award[]> => {
    const { programmeId } = params
    const url = `programmes/${programmeId}/awards`
    const result = await dao._http.get<HttpResponse<Award>>(url)
    return result.data.data
  },
  getMyProgrammeAwardsByProgramme: async (programmeId: string): Promise<AwardDto[]> => {
    const url = `/hustlers/me/awards/programme/${programmeId}`
    const result = await dao._http.get<HttpResponse<AwardDto>>(url)
    return result.data.data
  },
  getHustlerProgramme: async (params: { programmeId: string }): Promise<HustlerProgramme> => {
    const { programmeId } = params
    const url = `/hustlers/me/programmes/${programmeId}`
    const result = await dao._http.get<HttpResponse<HustlerProgramme>>(url)
    return result.data.data[0]
  },
  addToFavorites: async (params: { workoutId: string }): Promise<FavWorkout> => {
    const url = `/favorite-workouts`
    const result = await dao._http.post<HttpResponse<FavWorkout>>(url, params)
    const data = result.data.data[0]
    if (!data) throw new Error('Unable to add to favorites')
    const { workout, id } = data
    workout.favoriteId = id
    return { ...data, workout }
  },
  removeFavoriteWorkout: async (params: { id: string }): Promise<void> => {
    const { id } = params
    const url = `/favorite-workouts/${id}`
    await dao._http.delete<HttpResponse<void>>(url)
  },
  getProgrammeEquipmentList: async (params: { programmeId: string }): Promise<WorkoutEquipmentPiece[]> => {
    const { programmeId } = params
    const url = `/programmes/${programmeId}/equipment-list`
    const result = await dao._http.get<HttpResponse<WorkoutEquipmentPiece>>(url)
    return result.data.data
  },
  getWorkoutEquipmentPieces: async (): Promise<WorkoutEquipmentPiece[]> => {
    const url = `/workout-equipment-pieces`
    const result = await dao._http.get<HttpResponse<WorkoutEquipmentPiece>>(url)
    return result.data.data
  },
  listRunningSeries: async (): Promise<RunningSeries[]> => {
    const url = `/running-series`
    const result = await dao._http.get<HttpResponse<RunningSeries>>(url)
    return result.data.data
  },
  getTrainingSeriesById: async (params: { trainingSeriesId: string }): Promise<RunningSeriesWithWorkouts> => {
    const { trainingSeriesId } = params
    const url = `/running-series/${trainingSeriesId}`
    const result = await dao._http.get<HttpResponse<RunningSeriesWithWorkouts>>(url)
    return result.data.data[0]
  },
  getFavoriteWorkouts: async (): Promise<FavWorkout[]> => {
    const url = '/favorite-workouts'
    const result = await dao._http.get<HttpResponse<FavWorkout>>(url)
    return result.data.data.map((item) => {
      const { workout, id } = item
      workout.favoriteId = id
      return { ...item, workout }
    })
  },
  getMyCurrentMonthWorkoutStats: async (): Promise<WorkoutStats[]> => {
    const url = 'hustlers/me/stats-breakdown/current-month'
    const result = await dao._http.get<HttpResponse<WorkoutStats>>(url)
    return result.data.data
  },
  getMyProgrammeAwards: async (): Promise<AwardDto[]> => {
    const url = '/hustlers/me/awards/programme-awards'
    const result = await dao._http.get<HttpResponse<AwardDto>>(url)
    return result.data.data
  },
  getCompletedChallenges: async (params?: { limit?: number }): Promise<CompletedChallenge[]> => {
    const { limit } = params ?? {}
    const url = `/hustlers/me/challenges/completed`
    const result = await dao._http.get<HttpResponse<CompletedChallenge>>(url, { params: { limit: limit } })
    return result.data.data
  },
  getNotYetCompletedChallenges: async (): Promise<NotCompletedChallenge[]> => {
    const url = '/hustlers/me/challenges/not-completed'
    const result = await dao._http.get<HttpResponse<NotCompletedChallenge>>(url)
    return result.data.data
  },
  listWorkoutEquipmentGroups: async (): Promise<WorkoutEquipmentGroup[]> => {
    const url = '/workout-equipment-groups'
    const result = await dao._http.get<HttpResponse<WorkoutEquipmentGroup>>(url)
    return result.data.data
  },
  getWorkoutStyle: async (params: { workoutStyleId: string }): Promise<WorkoutStyle> => {
    const { workoutStyleId } = params
    const url = `/workout-styles/${workoutStyleId}`
    const result = await dao._http.get<HttpResponse<WorkoutStyle>>(url)
    return result.data.data[0]
  },
  listEducationHubCategories: async (): Promise<EducationHubCategory> => {
    const url = `/education-hub-categories`
    const result = await dao._http.get<HttpResponse<EducationHubCategory>>(url)
    return result.data.data[0]
  },
  listWorkoutDurations: async (): Promise<WorkoutDurationType[]> => {
    const url = `/workout-durations`
    const result = await dao._http.get<HttpResponse<WorkoutDurationType>>(url)
    return result.data.data
  },
}

export interface WorkoutEquipment {
  piece: EquipmentPiece
  equipmentPieceId: string
  optional: boolean
  quantity: number
}

export enum EquipmentPiece {
  BOOTY_BAND = 'booty_band',
  RESISTANCE_BAND = 'resistance_band',
  DUMBBELL = 'dumbbell',
  LIGHT_DUMBBELL = 'light_dumbbell',
  MEDIUM_DUMBBELL = 'medium_dumbbell',
  HEAVY_DUMBBELL = 'heavy_dumbbell',
  LIGHT_MEDIUM_DUMBBELL = 'light_medium_dumbbell',
  MEDIUM_HEAVY_DUMBBELL = 'medium_heavy_dumbbell',
  LIGHT_MEDIUM_HEAVY_DUMBBELLS = 'light_medium_heavy_dumbbell',
}

export enum EquipmentCategory {
  BAND = 'band',
  BODYWEIGHT = 'bodyweight',
  DUMBBELL = 'dumbbell',
}

export enum WorkoutIntensity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type WorkoutDuration = {
  id: string
  name: string
  label: string
  durationValue: number
  durationUnit: string
  filterIconPublicFileId?: string
  filterActiveIconPublicFileId?: string
  isExpressDuration: boolean
  isFilterable: boolean
}

export type WorkoutSummary = {
  workout: Workout
  date: string
}

export type Workout = {
  id: string
  title: string
  description?: string
  typeId: string
  bodyPartIds: string[]
  videoUrl?: string
  previewVideoUrl?: string
  imagePublicFileId: string
  equipment: WorkoutEquipment[]
  intensity: WorkoutIntensity
  durationId: string
  duration?: string
  availableAt: string
  favoriteId?: string
  intensityId?: string
  playlistUrl?: string
  trainers: Partial<Trainer>[]
}
export type WorkoutType = {
  id: string
  name: string
  label: string
  filterIconPublicFileId?: string
  filterActiveIconPublicFileId?: string
}
export type WorkoutStyle = {
  id: string
  name: string
  label: string
  filterIconPublicFileId?: string
  filterActiveIconPublicFileId?: string
}
export type WorkoutSection = {
  id: string
  label: string
}

export type BodyPart = {
  id: string
  name: string
  filterIconPublicFileId?: string
  filterActiveIconPublicFileId?: string
  parentBodyPartId?: string
}

export type CompletedWorkout = {
  id: string
  workout: Workout
  hustler: Partial<Hustler>
  hustleScore: number
  calories: number
  heartRate: number
  completedAt: string
}

export type HustlerCompletedWorkout = {
  completedWorkoutId: string
  hustler: {
    id: string
    name: string
    username?: string
    profilePhotoFileId?: string
  }
  metrics?: {
    hustleScore: number
    calories: number
    heartRate: number
  }
  completedAt?: number
  completedWorkoutCount?: number
}

export type CompletedByHustlersResponse = {
  data: HustlerCompletedWorkout[]
  page: number
  pageSize: number
  totalSize: number
  totalPages: number
}

export type StartedProgrammesFromLibrary = {
  data: HustlerProgramme[]
  page: number
  pageSize: number
  totalSize: number
  totalPages: number
}

export type Hustler = {
  id: string
  createdAt: Date
  updatedAt: Date
  createdById?: string
  updatedById?: string
  name: string
  authUserId: string
  email: string
  phoneNumber?: string
  appTourCompleted?: boolean
  profile: HustlerProfile
  // subscriptions: Partial<HustlerSubscription>[]
  // stats: Partial<HustlerStats>[]
  // friends: Partial<Hustler>[]
  // posts: Partial<Post>[]
  // awards: Partial<Award>[]
}
/**
 * How often a non-subscriber is prompted to upgrade after finishing a free workout.
 * Server-published so the cadence can change without a release.
 */
export enum FeaturedUpsellMode {
  EVERY = 'EVERY',
  FIRST_N_THEN_COOLDOWN = 'FIRST_N_THEN_COOLDOWN',
  OFF = 'OFF',
}

export type FeaturedUpsell = {
  mode: FeaturedUpsellMode
  /** Only meaningful under `FIRST_N_THEN_COOLDOWN`; always present. */
  firstN: number
  /** Only meaningful under `FIRST_N_THEN_COOLDOWN`; always present. */
  cooldownDays: number
}

export type HustlerConfig = {
  showWorkoutDates: boolean
  newWorkoutsTitle: string
  showNewMonthlyWorkouts: boolean
  /**
   * The single server-side access predicate. This is the only thing that decides whether an
   * account may play a workout it is not specifically entitled to — do not derive an
   * equivalent locally.
   */
  hasFullCatalogAccess: boolean
  /**
   * Whether `hasFullCatalogAccess` is an ANSWER or a GUESS. The server predicate fails open,
   * so a failed RevenueCat lookup arrives here as `hasFullCatalogAccess: true` with this
   * `false`. Anything deciding whether to HIDE a surface needs both — see `useCatalogAccess`.
   *
   * Optional because a backend older than GH-866 does not send it; absent counts as resolved,
   * which is exactly how the app behaved before the field existed.
   */
  catalogAccessResolved?: boolean
  featuredUpsell: FeaturedUpsell
}

/**
 * A card on the featured shelf. Deliberately NOT `Workout`: this is served to signed-out
 * visitors, so everything a card renders is inlined rather than left as an id to resolve
 * through `SessionContext` (which does not exist on the login page) or the guarded
 * `/trainers` route.
 */
export type FeaturedWorkout = {
  id: string
  title: string
  description?: string
  durationId: string
  /**
   * Display string for the duration. In current data this is the bare number (`'10'`,
   * `'40'`) — the unit suffix is ours to add, exactly as elsewhere in the app.
   */
  duration: string
  durationValue: number
  /** The stored enum: lower-case plural, e.g. `'minutes'`. */
  durationUnit: string
  intensity: string
  intensityId?: string
  imagePublicFileId?: string
  /** Always present on this endpoint — every row in it is free by definition. */
  videoUrl?: string
  /**
   * The workout's music playlist (GH-918).
   *
   * **Absent, never blank.** The server normalises `NULL`, `''` and whitespace to an
   * omitted key, because the stored column holds `''` — not `NULL` — for a workout with no
   * playlist (15 of 1038 on dev). So the render test is **presence**: a `!== null` check
   * would pass on `''` and put back the live-looking control with no destination that
   * GH-907 exists to remove.
   */
  playlistUrl?: string
  trainers: { id: string; name: string; imagePublicFileId?: string }[]
  equipment: { id: string; piece: string; quantity: number; optional: boolean }[]
  free: true
}
export enum Gender {
  MALE = 'male',
  FEMALE = 'female',
  NON_BINARY = 'non_binary',
  N_A = 'n_a',
}

export enum WeightUnit {
  KG = 'kg',
  LB = 'lb',
}

export enum HeightUnit {
  CM = 'cm',
  FT = 'ft',
}

export type HustlerProfile = {
  profilePhotoFileId?: string
  username?: string
  gender?: Gender | null
  dateOfBirth?: Date | null
  weight?: { value: number; unit: WeightUnit } | null
  height?: { value: number; unit: HeightUnit } | null
}

export type TrainerSocialProfiles = Record<string, string>

export interface Trainer {
  id: string
  firstName: string
  lastName: string
  description: string
  profilePhotoPublicFileId: string
  mediumProfilePhotoPublicFileId: string
  socialProfiles?: TrainerSocialProfiles
}

export type PromoCodeValidation = {
  valid: boolean
  discountLabel: string | null
  // Stripe product IDs the promo is restricted to. `null`/absent means it applies to every plan.
  appliesToProductIds?: string[] | null
}

export type Price = {
  id: string
  currency: string
  amount?: number
  recurring?: {
    interval: string
    intervalCount: number
  }
  product: {
    id: string
    name: string
    imageUrl: string | undefined
    trialDurationDays: number
  }
}

export type Store =
  | 'stripe'
  | 'ios'
  | 'android'
  /** RevenueCat / API value for Apple */
  | 'app_store'
  /** RevenueCat / API value for Google Play */
  | 'play_store'
  | 'test_store'
  | 'promotional'
export type SubscriptionType = '$rc_annual' | '$rc_monthly'
export type StoreSubscription = {
  store: Store
  productIdentifier: string
  type: SubscriptionType
}

export type Subscription = {
  id: string
  cancelAt?: number
  canceledAt?: number
  currentPeriodStart: number
  currentPeriodEnd: number
  status: string
  startDate: number
  items: SubscriptionItem[]
}

export type SubscriptionItem = {
  plan: {
    id: string
    amount: number
    currency: string
  }
  price: {
    id: string
    recurring?: {
      interval?: string
      intervalCount?: number
    }
  }
  product: {
    id: string
    name: string
  }
}

export type WeekPromoVideo = {
  title: string
  availableAt: number
  videoUrl: string
}

export enum ChallengeStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  TERMINATED = 'terminated',
}
export type ChallengeWorkoutsFilter = { type: string; category?: string } | { type: 'all' }
export type CompletionCriteria = {
  statistic?: string
  goal?: number
  filter?: ChallengeWorkoutsFilter
  timePeriod?: string
}

export type Challenge = {
  id: string
  title: string
  description: string
  imagePublicFileId: string
  startDate?: number
  endDate?: number
  criteria: CompletionCriteria
  status: ChallengeStatus
  categoryId: string
  autoStarted?: boolean
}

export interface ChallengeCategory {
  id: string
  name: string
  difficulty: number
}

export type CompletedChallenge = {
  challenge: Challenge
  completedAt?: number
}

export type WeeklyImage = {
  id: string
  dayInWeek: number
  publicFileId: string
}

export type ProgrammeWeekDescription = {
  order: number
  title: string
  description: string
}

export type Programme = {
  id: string
  title: string
  description: string
  shortDescription: string
  weekDescription: ProgrammeWeekDescription[]
  intensityId: string
  isDisabled: boolean
  availableAt: string
  imagePublicFileId: string
  order: number
  programmeWorkoutIds: string[]
  duration: {
    weeks: number
    classes: number
    seasons: number
    /** Present on some API responses (e.g. suggested programme). */
    shortestWorkoutDuration?: number
    longestWorkoutDuration?: number
  }
}

export type Intensity = {
  id: string
  name: string
  label: string
  filterIconPublicFileId: string
  filterActiveIconPublicFileId: string
  intensityGroup: string
}

export type InfoTabItem = {
  order: number
  title: string
  description: string
}

export interface Award {
  id: string
  name: string
  imagePublicFileId: string
}

export type ProgrammeDuration = {
  weeks: {
    count: number
  }
  classes: {
    count: number
    shortestDuration: string
    longestDuration: string
  }
}

export type ProgrammeWorkoutsAvailability = {
  onWeek: number
  isWeekLocked: boolean
  areProgrammeWorkoutsInWeekLocked: boolean
}

export interface ProgrammeWorkout {
  id: string
  order: number
  onWeek: number
  isDisabled: boolean
  isCompleted?: boolean
  workout: Workout
  workoutId: string
}

export type SuggestedSeriesSuggestionState = 'active' | 'exhausted' | 'inactive'

export type SuggestedSeries = {
  suggestionState: SuggestedSeriesSuggestionState
  programme: Programme | null
  nextProgrammeWorkout: ProgrammeWorkout | null
}

export type HustlerProgrammeEnrollmentSource = 'library' | 'suggested'

export type StartProgrammeParams = {
  programmeId: string
  enrollmentSource?: HustlerProgrammeEnrollmentSource
}

export type MotivationalVideo = {
  videoUrl: string
  order: number
  isDisabled: boolean
}

export type MotivationalImage = {
  imagePublicFileId: string
  order: number
  isDisabled: boolean
}

export interface HustlerProgramme {
  id: string
  startedAt: string
  programme: Programme
  hustler: Hustler
}

export interface CompletedProgrammeWorkout {
  id: string
  hustlerProgramme: Partial<HustlerProgramme>
  programme: Partial<Programme>
  programmeWorkout: Partial<ProgrammeWorkout>
  completedWorkout: Partial<CompletedWorkout>
  workout: Partial<Workout>
}

export type WorkoutEquipmentPiece = {
  piece: string
  quantity: number
  optional: boolean
  id: string
  name: string
  label?: string
  workoutEquipmentGroupId?: string
  workoutEquipmentGroup?: Partial<WorkoutEquipmentGroup>
}

export type WorkoutEquipmentGroup = {
  id: string
  name: string
  label: string
  order: number
  filterIconPublicFileId?: string
  filterActiveIconPublicFileId?: string
  isNoEquipmentGroup: boolean
}

export interface AwardDto {
  id: string
  count: number
  award: Award
}

export const hustlerProgrammeSchema = Joi.object({
  startedAt: Joi.number().optional().allow(null),
  stoppedAt: Joi.number().optional().allow(null),
  terminatedAt: Joi.number().optional().allow(null),
}).options({ allowUnknown: true })

export interface FavWorkout {
  id: string
  workout: Workout
  addedAt: number
}

export type ProgrammeWorkoutDuration = {
  id: string
  name: string
  label: string
  durationValue: number
  durationUnit: string
  filterIconPublicFileId: string
  filterActiveIconPublicFileId: string
  isExpressDuration?: boolean
  isFilterable?: boolean
}

export type RunningSeries = {
  id: string
  createdAt: string
  updatedAt: string
  isAvailable: boolean
  title: string
  subTitle: string
  description: string
  coverImagePublicFileId: string
  thumbnailImagePublicFileId: string
}

export type EducationHubCategory = {
  id: string
  createdAt: string
  updatedAt: string
  title: string
  order: number
  workouts: Workout[]
}

export type RunningSeriesWithWorkouts = RunningSeries & { workouts: Workout[] }

export interface WorkoutStats {
  completedWorkoutsCount: number
  awardsCount: number
  totalHustle: number
  totalCalories: number
}

export interface NotCompletedChallenge {
  category: ChallengeCategory
  challenges: {
    challenge: Challenge
    progress: number
  }[]
}

export type WorkoutDurationType = {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  label: string
  order: number
  durationValue: number
  durationUnit: string
  filterIconPublicFileId: string
  filterActiveIconPublicFileId: string
  isExpressDuration: boolean
  isFilterable: boolean
}
